import { DateTime } from 'luxon';
import { Chip } from './types';

/** Em dash used as a placeholder for missing values. */
const EM_DASH = '—';

/**
 * Format a date string (YYYY-MM-DD) into a human-readable format.
 * Returns an em dash for null/undefined/invalid values.
 */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return EM_DASH;
  }
  const d = DateTime.fromISO(dateStr);
  if (!d.isValid) {
    return EM_DASH;
  }
  return d.toFormat('LLL d, yyyy');
}

/**
 * Get display name for a chip's adopter/owner.
 */
export function getAdopterDisplayName(chip: Chip): string {
  if (chip.assignedTo === 'rescue') {
    return 'Rescue (in care)';
  }
  if (chip.adopterFirstName && chip.adopterLastName) {
    return `${chip.adopterFirstName} ${chip.adopterLastName}`;
  }
  return EM_DASH;
}

/**
 * Format an ISO timestamp into a compact relative string (e.g. "Now", "5 minutes ago", "3 hours ago", "2 days ago").
 * Uses luxon's toRelative() for proper locale-aware relative formatting.
 * Returns an em dash for null/undefined/invalid values.
 */
export function formatTimestamp(ts: string | null | undefined): string {
  if (!ts) {
    return EM_DASH;
  }
  const dt = DateTime.fromISO(ts);
  if (!dt.isValid) {
    return EM_DASH;
  }
  const diffMins = DateTime.now().diff(dt, 'minutes').minutes;
  if (diffMins < 2) {
    return 'Now';
  }
  return dt.toRelative() ?? EM_DASH;
}

/**
 * Format a date string into a relative "X hours/days ago" string (day-level granularity).
 * Uses luxon's toRelative() for proper locale-aware relative formatting.
 * Returns an em dash for null/undefined/invalid values.
 */
export function formatDaysAgo(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return EM_DASH;
  }
  const dt = DateTime.fromISO(dateStr);
  if (!dt.isValid) {
    return EM_DASH;
  }
  return dt.toRelative() ?? EM_DASH;
}

/**
 * US state abbreviations for address forms.
 */
export const US_STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'DC',
];

// ── Unit conversion ──────────────────────────

export const KG_TO_LBS = 2.20462;

export function kgToLbs(kg: number | null | undefined): number {
  if (kg == null || kg === 0) {
    return 0;
  }
  return Math.round(kg * KG_TO_LBS * 10) / 10;
}

export function lbsToKg(lbs: number | null | undefined): number {
  if (lbs == null || lbs === 0) {
    return 0;
  }
  return Math.round((lbs / KG_TO_LBS) * 10) / 10;
}

// ── Pet status helpers ──────────────────────────

export type PetStatus = 'rescue' | 'foster' | 'pending_adoption' | 'adopted';

export const STATUS_LABELS: Record<PetStatus, string> = {
  rescue: 'Rescue',
  foster: 'Fostered',
  pending_adoption: 'Pending Adoption',
  adopted: 'Adopted',
};

export const STATUS_COLORS: Record<PetStatus, string> = {
  rescue: 'grey',
  foster: 'blue',
  pending_adoption: 'amber',
  adopted: 'green',
};

export function speciesEmoji(species?: string | null): string {
  if (!species) {
    return '🐾';
  }
  const s = species.toUpperCase();
  if (s === 'DOG') {
    return '🐶';
  }
  if (s === 'CAT') {
    return '🐱';
  }
  return '🐾';
}
